import { colorMaps, ColorMap } from "./colormaps";

export { ColorMap } from "./colormaps";

export type SharedColorParams = {
  /** 0 = base, 1 = gradient */
  mode?: 0 | 1;
  /** Selected colormap */
  colorMap?: ColorMap;
  /** Colormap sampling domain, per theme (from getColormapDomain) */
  colorDomainLo?: number;
  colorDomainHi?: number;
  /** OKLab lightness floor for the colormap (from getColormapLightFloor); 0 = none */
  colorLightFloor?: number;
};

export const sharedColorVertexVaryings = `
  varying vec3 vBaseColor;
  varying float vScalar;
`;

/**
 * GLSL helper functions for vertex shaders
 * Provides convenient functions for setting color-related varyings
 */
export const sharedColorVertexHelpers = `
  void setBaseColor(vec3 color) {
    vBaseColor = color;
  }

  void setScalar(float index, float start, float end) {
    float denom = max(1.0, end - start);
    vScalar = clamp((index - start) / denom, 0.0, 1.0);
  }
`;

export const sharedColorFragmentPrelude = `
  varying vec3 vBaseColor;
  varying float vScalar;

  uniform float uColorMode;
  uniform float uColorMap;
  uniform float uColorDomainLo;
  uniform float uColorDomainHi;
  uniform float uColorLightFloor;

  ${colorMaps}

  // Lift a colormap stop's OKLab lightness toward uColorLightFloor, preserving
  // hue and chroma (a,b are untouched, only L). On the near-black dark canvas
  // this raises the darkest stops (magma/inferno start near-black) to a visible,
  // same-hue lighter shade instead of dropping the low end of the ramp. Mirrors
  // liftLightness() in colormap-gradients.ts so the mesh matches the DOM legend.
  vec3 srgbToLinear(vec3 c) {
    return mix(c / 12.92, pow((c + 0.055) / 1.055, vec3(2.4)), step(0.04045, c));
  }
  vec3 linearToSrgb(vec3 c) {
    c = max(c, 0.0);
    return mix(c * 12.92, 1.055 * pow(c, vec3(1.0 / 2.4)) - 0.055, step(0.0031308, c));
  }
  vec3 liftLightness(vec3 srgb, float floorL) {
    if (floorL <= 0.0) return srgb;
    vec3 lin = srgbToLinear(clamp(srgb, 0.0, 1.0));
    float l = 0.4122214708 * lin.r + 0.5363325363 * lin.g + 0.0514459929 * lin.b;
    float m = 0.2119034982 * lin.r + 0.6806995451 * lin.g + 0.1073969566 * lin.b;
    float s = 0.0883024619 * lin.r + 0.2817188376 * lin.g + 0.6299787005 * lin.b;
    float l_ = pow(l, 1.0 / 3.0);
    float m_ = pow(m, 1.0 / 3.0);
    float s_ = pow(s, 1.0 / 3.0);
    float L = 0.2104542553 * l_ + 0.7936177850 * m_ - 0.0040720468 * s_;
    float a = 1.9779984951 * l_ - 2.4285922050 * m_ + 0.4505937099 * s_;
    float b = 0.0259040371 * l_ + 0.7827717662 * m_ - 0.8086757660 * s_;
    float Lp = floorL + L * (1.0 - floorL);
    float li = Lp + 0.3963377774 * a + 0.2158037573 * b;
    float mi = Lp - 0.1055613458 * a - 0.0638541728 * b;
    float si = Lp - 0.0894841775 * a - 1.2914855480 * b;
    li = li * li * li; mi = mi * mi * mi; si = si * si * si;
    vec3 outLin = vec3(
      4.0767416621 * li - 3.3077115913 * mi + 0.2309699292 * si,
      -1.2684380046 * li + 2.6097574011 * mi - 0.3413193965 * si,
      -0.0041960863 * li - 0.7034186147 * mi + 1.7076147010 * si
    );
    return clamp(linearToSrgb(outLin), 0.0, 1.0);
  }

  vec3 computeSharedColor(vec3 baseColor, float t) {
    if (uColorMode > 0.5) {
      float tt = uColorDomainLo + clamp(t, 0.0, 1.0) * (uColorDomainHi - uColorDomainLo);
      vec3 col;
      if (uColorMap < 0.5) col = viridisColor(tt);
      else if (uColorMap < 1.5) col = turboColor(tt);
      else if (uColorMap < 2.5) col = jetColor(tt);
      else if (uColorMap < 3.5) col = magmaColor(tt);
      else if (uColorMap < 4.5) col = infernoColor(tt);
      else col = grayscaleColor(tt);
      return liftLightness(col, uColorLightFloor);
    }
    return baseColor;
  }
`;

export const createSharedColorUniforms = () => ({
  uColorMode: { value: 0 },
  uColorMap: { value: ColorMap.TURBO },
  uColorDomainLo: { value: 0 },
  uColorDomainHi: { value: 1 },
  uColorLightFloor: { value: 0 },
});

export const applySharedColorParams = (
  uniforms: Record<string, { value: number | ColorMap }>,
  params?: SharedColorParams
) => {
  if (!params || !uniforms) return;

  if (typeof params.mode === "number") uniforms.uColorMode.value = params.mode;
  if (typeof params.colorMap === "number") uniforms.uColorMap.value = params.colorMap;
  if (typeof params.colorDomainLo === "number" && uniforms.uColorDomainLo) {
    uniforms.uColorDomainLo.value = params.colorDomainLo;
  }
  if (typeof params.colorDomainHi === "number" && uniforms.uColorDomainHi) {
    uniforms.uColorDomainHi.value = params.colorDomainHi;
  }
  if (typeof params.colorLightFloor === "number" && uniforms.uColorLightFloor) {
    uniforms.uColorLightFloor.value = params.colorLightFloor;
  }
};
